import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, Cpu, ShieldCheck, AlertTriangle, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../../lib/backend";

const API = BACKEND_URL + "/api";

const short = (m) => (m || "").replace("claude-", "").replace("gpt-", "gpt ");
const usd = (v) => `$${Number(v || 0).toFixed(2)}`;
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

function Pill({ ok, children, dm }) {
  const cls = ok
    ? (dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-50 text-emerald-700")
    : (dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-50 text-amber-700");
  return <span className={`px-2 py-0.5 rounded-full text-[11px] category-tag ${cls}`}>{children}</span>;
}

function GateChecks({ gate, dm }) {
  if (!gate) return null;
  return (
    <ul className="mt-2 space-y-1" data-testid="bakeoff-gate-checks">
      {Object.entries(gate.checks || {}).map(([k, c]) => (
        <li key={k} className={`text-xs flex items-center gap-2 ${dm ? "text-slate-300" : "text-[#3c494b]"}`} data-testid={`bakeoff-check-${k}`}>
          <Pill ok={c.pass} dm={dm}>{c.pass ? "pass" : "fail"}</Pill>
          <span className="font-mono">{k}</span>
          <span>{typeof c.value === "number" ? (c.value <= 1 && !["over_250_words", "refusals"].includes(k) ? pct(c.value) : c.value) : String(c.value ?? "—")}</span>
          {c.incumbent != null && <span className="opacity-60">vs incumbent {typeof c.incumbent === "number" && c.incumbent <= 1 ? pct(c.incumbent) : c.incumbent}</span>}
          <span className="opacity-50">· {String(c.threshold)}</span>
        </li>
      ))}
    </ul>
  );
}

function BakeoffPanel({ dm, authHeader }) {
  const [bk, setBk] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/summary-model/bakeoff`, { headers: authHeader() });
      setBk(r.data);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error("Bake-off status unavailable");
      setBk(null);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (bk?.status !== "running" && bk?.status !== "queued") return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [bk?.status, load]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await axios.post(`${API}/admin/summary-model/bakeoff`, { n: 100 }, { headers: authHeader() });
      toast.success(r.data.status === "started" ? `Bake-off ${r.data.id} started (100 articles, ~5–8 min)` : `Bake-off already running (${r.data.id})`);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start bake-off");
    } finally {
      setStarting(false);
    }
  };

  const c = bk?.candidate_stats || {};
  const i = bk?.incumbent_stats || {};
  return (
    <div className={`mt-5 rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`} data-testid="summary-bakeoff-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FlaskConical className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
          <span className={`text-sm font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Haiku 5.5 summary gate</span>
          {bk && <Pill ok={bk.status === "completed" && bk.gate?.passed} dm={dm}>
            {bk.status === "completed" ? (bk.gate?.passed ? "GATE PASSED" : "GATE FAILED") : bk.status}
          </Pill>}
        </div>
        <button onClick={start} disabled={starting || bk?.status === "running"} data-testid="bakeoff-start-btn"
          className={`px-3 py-1.5 rounded-full text-xs category-tag flex items-center gap-1.5 disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25" : "bg-[#006874]/10 text-[#006874] hover:bg-[#006874]/20"}`}>
          {starting || bk?.status === "running" ? <RefreshCw className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />}
          {bk?.status === "running" ? `Running ${bk.progress?.done ?? 0}/${bk.progress?.total ?? "…"}` : "Run 100-article bake-off"}
        </button>
      </div>
      {!bk && <p className={`mt-2 text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>No bake-off yet. Default stays gpt-4o-mini until the gate passes.</p>}
      {bk?.status === "completed" && (
        <>
          <div className={`mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs ${dm ? "text-slate-300" : "text-[#3c494b]"}`} data-testid="bakeoff-stats">
            {[["English", pct(c.english_rate), pct(i.english_rate)], ["Median words", c.median_words, i.median_words],
              ["Number precision", pct(c.number_precision), pct(i.number_precision)], ["Entity overlap", pct(c.entity_overlap), pct(i.entity_overlap)],
              ["Latency p95", `${c.latency_p95_ms ?? "—"} ms`, `${i.latency_p95_ms ?? "—"} ms`], ["Cost / 1,000", `$${c.cost_usd_per_1000 ?? "—"}`, `$${i.cost_usd_per_1000 ?? "—"}`],
              ["Refusals", bk.refusals?.candidate ?? 0, "—"], ["Sample", `${bk.sample?.evaluated} evaluated`, `${bk.sample?.dropped_content_problem || 0} dropped`]].map(([l, a, b]) => (
              <div key={l} className={`rounded-lg px-2.5 py-2 ${dm ? "bg-[#1a2122]" : "bg-white"}`}>
                <div className="opacity-60 text-[10px] uppercase tracking-wide">{l}</div>
                <div className="font-mono">{a}</div>
                <div className="opacity-50 font-mono text-[10px]">incumbent {b}</div>
              </div>
            ))}
          </div>
          <GateChecks gate={bk.gate} dm={dm} />
        </>
      )}
      {bk?.status === "failed" && <p className="mt-2 text-xs text-red-400" data-testid="bakeoff-error">Failed: {bk.error}</p>}
    </div>
  );
}

export default function ModelRegistryCard({ dm, authHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/models`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      toast.error("Model registry unavailable");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const runWatchdog = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${API}/admin/models/watchdog/run`, {}, { headers: authHeader() });
      toast[r.data.status === "ok" ? "success" : "warning"](r.data.detail);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Watchdog check failed");
    } finally {
      setChecking(false);
    }
  };

  const wd = data?.watchdog?.check;
  const q = data?.quality;
  const spend = data?.spend;
  const anthropicTasks = (data?.tasks || []).filter((t) => t.provider === "anthropic");
  const openaiTasks = (data?.tasks || []).filter((t) => t.provider !== "anthropic");

  return (
    <div className={`rounded-2xl p-6 mt-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }} data-testid="model-registry-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
            <Cpu className={`w-5 h-5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
          </div>
          <div>
            <h3 className={`editorial-heading text-lg font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Model registry</h3>
            <p className={`text-sm ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              One place for every LLM task — <span className="font-mono">backend/models_registry.py</span>. Override per task with <span className="font-mono">TI_MODEL_&lt;TASK&gt;</span> / <span className="font-mono">TI_EFFORT_&lt;TASK&gt;</span>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="model-registry-refresh" className={`p-2 rounded-full ${dm ? "hover:bg-[#252e2f] text-slate-300" : "hover:bg-[#f3f4f5] text-[#3c494b]"}`}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={runWatchdog} disabled={checking} data-testid="model-watchdog-run" className={`px-3 py-1.5 rounded-full text-xs category-tag flex items-center gap-1.5 disabled:opacity-50 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`}>
            <ShieldCheck className="w-3 h-3" />{checking ? "Checking…" : "Check deprecations now"}
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="model-registry-lineup">
            {Object.entries(data.lineup || {}).map(([k, v]) => (
              <span key={k} className={`px-2.5 py-1 rounded-full text-xs font-mono ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`}>{k}: {v}</span>
            ))}
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <div className={`rounded-xl p-3 text-xs ${dm ? "bg-[#252e2f] text-slate-300" : "bg-[#f3f4f5] text-[#3c494b]"}`} data-testid="model-watchdog-status">
              <div className="flex items-center gap-2 mb-1">
                {wd?.status === "ok" ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                <span className="font-medium">Anthropic deprecations</span>
                <Pill ok={!wd || wd.status === "ok"} dm={dm}>{wd?.status || "not checked"}</Pill>
              </div>
              <div className="opacity-80">{wd?.detail || "Weekly watchdog reads the official deprecations table (state + retirement date) for every model in use."}</div>
              {data.watchdog?.rows && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {Object.entries(data.watchdog.rows).map(([slug, r]) => (
                    <span key={slug} className={`px-2 py-0.5 rounded-full font-mono text-[10px] ${dm ? "bg-[#1a2122]" : "bg-white"}`} data-testid={`watchdog-row-${slug}`}>
                      {short(slug)} · {r.state}{r.retirement ? ` · ret. ≥ ${r.retirement}` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className={`rounded-xl p-3 text-xs ${dm ? "bg-[#252e2f] text-slate-300" : "bg-[#f3f4f5] text-[#3c494b]"}`} data-testid="llm-quality-status">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">Refusals / fallbacks (24h)</span>
                <Pill ok={q?.status === "ok"} dm={dm}>{q?.status || "—"}</Pill>
              </div>
              <div className="opacity-80">{q?.detail}</div>
              {spend && (
                <div className="mt-2" data-testid="llm-spend-summary">
                  <span className="font-medium">Spend this week:</span> {usd(spend.total_current_usd)} <span className="opacity-60">(previous week {usd(spend.total_previous_usd)})</span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs" data-testid="model-registry-table">
              <thead>
                <tr className={`text-left ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                  <th className="py-1.5 pr-3 font-medium">Task</th>
                  <th className="py-1.5 pr-3 font-medium">Model</th>
                  <th className="py-1.5 pr-3 font-medium">Effort</th>
                  <th className="py-1.5 pr-3 font-medium">Max tokens</th>
                  <th className="py-1.5 pr-3 font-medium">Fallback</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Week $</th>
                </tr>
              </thead>
              <tbody className={dm ? "text-slate-200" : "text-[#191c1d]"}>
                {[...anthropicTasks, ...openaiTasks].map((t) => {
                  const row = (spend?.rows || []).find((r) => r.task === t.task);
                  return (
                    <tr key={t.task} className={`border-t ${dm ? "border-white/5" : "border-black/5"}`} data-testid={`registry-row-${t.task}`}>
                      <td className="py-1.5 pr-3"><div>{t.label}</div><div className="font-mono opacity-50 text-[10px]">{t.task}</div></td>
                      <td className="py-1.5 pr-3 font-mono">{short(t.model)}{t.overridden && <span className="ml-1 text-amber-400" title={`env override (default ${t.default_model})`}>*</span>}</td>
                      <td className="py-1.5 pr-3 font-mono opacity-80">{t.effort || "default"}</td>
                      <td className="py-1.5 pr-3 font-mono opacity-80">{t.max_tokens ?? "—"}</td>
                      <td className="py-1.5 pr-3 font-mono opacity-80">{t.fallback_model ? short(t.fallback_model) : "—"}</td>
                      <td className="py-1.5 pr-3 font-mono text-right">{row ? `${usd(row.current_usd)} ` : "—"}{row && <span className="opacity-50">/ {usd(row.previous_usd)}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <BakeoffPanel dm={dm} authHeader={authHeader} />
        </>
      )}
    </div>
  );
}
